import React, { useState, useRef } from 'react';
import { 
  downloadChainsTemplate, downloadSectionsTemplate, downloadCategoriesTemplate,
  downloadSkusTemplate, downloadStoresTemplate, downloadSkuPricesTemplate, downloadStoreSectionSkusTemplate,
  uploadChainsFile, uploadSectionsFile, uploadCategoriesFile,
  uploadSkusFile, uploadStoresFile, uploadSkuPricesFile, uploadStoreSectionSkusFile
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Download, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Building2, Layers, Tag, Package, Store, DollarSign, Settings } from 'lucide-react';
import { toast } from 'sonner';

const UPLOAD_CONFIGS = [
  {
    key: 'chains',
    title: 'Cadenas',
    description: 'Cadenas de supermercados (Walmart, Soriana, etc.)',
    icon: Building2,
    color: 'blue',
    downloadTemplate: downloadChainsTemplate,
    uploadFile: uploadChainsFile,
    fileName: 'plantilla_cadenas.xlsx',
    order: 1
  },
  {
    key: 'sections',
    title: 'Secciones',
    description: 'Secciones de tienda (Lácteos, Bebidas, etc.)',
    icon: Layers,
    color: 'purple',
    downloadTemplate: downloadSectionsTemplate,
    uploadFile: uploadSectionsFile,
    fileName: 'plantilla_secciones.xlsx',
    order: 2
  },
  {
    key: 'categories',
    title: 'Categorías',
    description: 'Categorías de productos (Leche, Yogurt, etc.)',
    icon: Tag,
    color: 'orange',
    downloadTemplate: downloadCategoriesTemplate,
    uploadFile: uploadCategoriesFile,
    fileName: 'plantilla_categorias.xlsx',
    order: 3
  },
  {
    key: 'skus',
    title: 'SKUs / Productos',
    description: 'Productos con código de barras, marca y categoría',
    icon: Package,
    color: 'green',
    downloadTemplate: downloadSkusTemplate,
    uploadFile: uploadSkusFile,
    fileName: 'plantilla_skus.xlsx',
    order: 4
  },
  {
    key: 'stores',
    title: 'Tiendas',
    description: 'Tiendas/sucursales con ubicación y geocerca',
    icon: Store,
    color: 'cyan',
    downloadTemplate: downloadStoresTemplate,
    uploadFile: uploadStoresFile,
    fileName: 'plantilla_tiendas.xlsx',
    order: 5
  },
  {
    key: 'sku-prices',
    title: 'Precios por Cadena',
    description: 'PVP de cada SKU según la cadena',
    icon: DollarSign,
    color: 'emerald',
    downloadTemplate: downloadSkuPricesTemplate,
    uploadFile: uploadSkuPricesFile,
    fileName: 'plantilla_precios.xlsx',
    order: 6
  },
  {
    key: 'store-section-skus',
    title: 'Configuración Tienda-SKUs',
    description: 'SKUs por tienda/sección con stock mínimo y crítico',
    icon: Settings,
    color: 'slate',
    downloadTemplate: downloadStoreSectionSkusTemplate,
    uploadFile: uploadStoreSectionSkusFile,
    fileName: 'plantilla_config_tienda_skus.xlsx',
    order: 7
  }
];

export default function BulkUploadPage() {
  const [uploading, setUploading] = useState({});
  const [results, setResults] = useState({});
  const fileInputRefs = useRef({});

  const handleDownload = async (config) => {
    try {
      const response = await config.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', config.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Plantilla ${config.title} descargada`);
    } catch (error) {
      toast.error(`Error descargando plantilla de ${config.title}`);
    }
  };

  const handleUpload = async (config, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [config.key]: true }));
    setResults(prev => ({ ...prev, [config.key]: null }));
    
    try {
      const response = await config.uploadFile(file);
      const result = response.data;
      setResults(prev => ({ ...prev, [config.key]: result }));
      
      if (result.errors?.length > 0) {
        toast.warning(`${config.title}: ${result.created} creados, ${result.updated} actualizados, ${result.errors.length} errores`);
      } else {
        toast.success(`${config.title}: ${result.created} creados, ${result.updated} actualizados`);
      }
    } catch (error) {
      toast.error(`Error importando ${config.title}`);
      setResults(prev => ({ ...prev, [config.key]: { error: error.message } }));
    } finally {
      setUploading(prev => ({ ...prev, [config.key]: false }));
      if (fileInputRefs.current[config.key]) {
        fileInputRefs.current[config.key].value = '';
      }
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 hover:border-blue-400',
      purple: 'bg-purple-50 border-purple-200 hover:border-purple-400',
      orange: 'bg-orange-50 border-orange-200 hover:border-orange-400',
      green: 'bg-green-50 border-green-200 hover:border-green-400',
      cyan: 'bg-cyan-50 border-cyan-200 hover:border-cyan-400',
      emerald: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400',
      slate: 'bg-slate-50 border-slate-200 hover:border-slate-400'
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClass = (color) => {
    const colors = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      orange: 'text-orange-600',
      green: 'text-green-600',
      cyan: 'text-cyan-600',
      emerald: 'text-emerald-600',
      slate: 'text-slate-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6" data-testid="bulk-upload-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Carga Masiva</h1>
        <p className="text-slate-500">Importa datos desde archivos Excel. Descarga primero la plantilla para ver el formato correcto.</p>
      </div>

      <Alert className="border-blue-200 bg-blue-50">
        <FileSpreadsheet className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Orden recomendado de carga:</strong> Cadenas - Secciones - Categorías - SKUs - Tiendas - Precios - Config Tienda-SKUs
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {UPLOAD_CONFIGS.map((config) => {
          const Icon = config.icon;
          const result = results[config.key];
          const isUploading = uploading[config.key];
          
          return (
            <Card key={config.key} className={`border-2 transition-colors ${getColorClasses(config.color)}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${getIconColorClass(config.color)}`} />
                    <CardTitle className="text-lg">{config.title}</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs">{config.order}</Badge>
                </div>
                <CardDescription>{config.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownload(config)} className="flex-1" data-testid={`download-${config.key}-template`}>
                    <Download className="w-4 h-4 mr-1" /> Plantilla
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fileInputRefs.current[config.key]?.click()} disabled={isUploading} className="flex-1" data-testid={`upload-${config.key}-file`}>
                    <Upload className="w-4 h-4 mr-1" /> {isUploading ? 'Cargando...' : 'Subir'}
                  </Button>
                  <input type="file" ref={(el) => fileInputRefs.current[config.key] = el} onChange={(e) => handleUpload(config, e.target.files?.[0])} accept=".xlsx,.xls" className="hidden" />
                </div>
                
                {result && (
                  <div className="text-sm rounded-md p-2 bg-white border">
                    {result.error ? (
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        Error: {result.error}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Creados: {result.created}, Actualizados: {result.updated}</span>
                        </div>
                        {result.errors?.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-amber-600 cursor-pointer">{result.errors.length} errores (ver detalles)</summary>
                            <ul className="mt-1 text-xs text-slate-600 max-h-32 overflow-y-auto">
                              {result.errors.map((err, i) => (<li key={i} className="py-0.5">• {err}</li>))}
                            </ul>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">¿Qué significa cada campo especial?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <h4 className="font-semibold text-slate-900">🔴 CRÍTICO (es_critico)</h4>
            <p className="text-slate-600">Cuando un SKU está marcado como CRÍTICO y tiene stock bajo: se genera alerta de ALTA PRIORIDAD, el mercaderista DEBE registrar una acción correctiva y tomar foto de evidencia antes de hacer check-out. Útil para productos de alta rotación o margen alto.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">📊 ORDEN (orden)</h4>
            <p className="text-slate-600">Define la secuencia en que aparecen las secciones cuando el mercaderista reporta inventario por WhatsApp. Secciones con menor orden aparecen primero.</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">📍 GEOCERCA (radio_geocerca_m)</h4>
            <p className="text-slate-600">Radio en metros alrededor de la tienda. El mercaderista debe estar dentro de este radio para hacer check-in/check-out.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
